<template>
  <div id="form-step">
    <b-row class="row">
      <b-col cols="4">
        <text-input
          :label="$t('CNTRCT_ID')"
          disabled
          borderbottom
          placeholder=""
        />
      </b-col>
      <b-col cols="4">
        <toggle-box ref="contractMaturesDateBox">
          <template v-slot:control>
            <text-input
              v-model="maturesDateInput"
              :label="$t('CONTRACT_CREATED_DATE')"
              icon-position="right"
              :disabled="!edit"
              borderbottom
              placeholder=""
            >
              <template v-slot:icon>
                <font-awesome-icon @click="showMaturesDateBox" class="icon" :icon="faCalendarAlt" />
              </template>
            </text-input>
          </template>
          <template v-slot:content>
            <calendar
              :date="moment(maturesDateInput, DISPLAY_DATE_FORMAT).toISOString() || undefined"
              @dateChange="setMaturesDateInput"
            />
          </template>
        </toggle-box>
      </b-col>
    </b-row>
    <b-row class="row">
      <b-col cols="4">
        <div
          class="floating-label"
          :class="{
            active : !!productInput,
          }"
        >
          <select-input
            ref="productSelectInput"
            tabindex="-1"
            :items="productItems"
            v-model="productInput"
            required
            :disabled="!edit"
            borderless
          />
          <span class="highlight"></span>
          <label>{{ $t("PACKAGE") }}<span>*</span></label>
        </div>
      </b-col>
      <b-col cols="4">
        <text-input
          :label="$t('CNTRCT_DUE_DATE')"
          v-model="dueDate"
          disabled
          borderbottom
          placeholder=""
        />
      </b-col>
    </b-row>
    <b-row class="row">
      <b-col cols="4">
        <text-input
          :label="$t('INTEREST_RATE')"
          v-model="specialInterestValueInput"
          :disabled="interestValueDisabled || !edit"
          :validate="validateSpecialInterestValueInput"
          required
          borderbottom
          placeholder=""
        />
      </b-col>
      <b-col cols="4">
        <text-input
          ref="approveAmountTextInput"
          :label="$t('APPROVED_AMOUNT')"
          v-model="approvedAmountInput"
          :validate="validateApproveAmount"
          :disabled="!edit"
          :hint="changeDigitToViText(approvedAmountInput)"
          borderbottom
          icon-position="right"
          placeholder=""
        />
      </b-col>
      <b-col cols="4">
        <text-input
          :label="$t('ROUNDING_AMOUNT')"
          v-model="roundingFeeInput"
          :hint="changeDigitToViText(roundingFeeInput)"
          disabled
          borderbottom
          placeholder=""
        />
      </b-col>
    </b-row>
    <b-row class="row">
      <b-col cols="4">
        <text-input
          v-if="promotionName !== null"
          :label="$t('PROMOTION')"
          v-model="promotionName"
          disabled
          borderbottom
          placeholder=""
        />
      </b-col>
      <b-col cols="4">
        <text-input
          :label="$t('PRINCIPAL')"
          v-model="principalAmount"
          :hint="changeDigitToViText(principalAmount)"
          disabled
          borderbottom
          placeholder=""
        />
      </b-col>
      <b-col cols="4">
        <text-input
          :label="$t('CAR_STORING_FEE')"
          v-model="warehousingFeeInput"
          :hint="changeDigitToViText(warehousingFeeInput)"
          :disabled="!edit"
          borderbottom
          placeholder=""
        />
      </b-col>
    </b-row>
    <b-row class="row">
      <b-col cols="4">
        <text-input
          :label="$t('DISCOUNT_MONEY')"
          v-if="promotionName !== null"
          v-model="interestDiscountAmount"
          :hint="changeDigitToViText(interestDiscountAmount)"
          disabled
          borderbottom
          placeholder=""
        />
      </b-col>
      <b-col cols="4">
        <text-input
          :label="$t('INTEREST_AMOUNT')"
          v-model="interestAmount"
          :hint="changeDigitToViText(interestAmount)"
          disabled
          borderbottom
          placeholder=""
        />
      </b-col>
      <b-col cols="4">
        <text-input
          :label="$t('RECEIVED_AMOUNT')"
          v-model="receivedAmount"
          :hint="changeDigitToViText(receivedAmount)"
          disabled
          borderbottom
          placeholder=""
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import moment from 'moment';
import { computed, onMounted, ref, watch } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';

import Calendar from '@/components/Calendar';
import TextInput from '@/components/TextInput';
import ToggleBox from '@/components/ToggleBox';
import SelectInput from '@/components/SelectInput';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { DISPLAY_DATE_FORMAT, DATE_FORMAT, MILLION } from '@/consts';
import { getRoundPrincipal, getInterestValue, getRoundFee, getInterestDiscountAmount } from '@/helpers/contractCalculation';
import { changeDigitToViText } from '@/helpers';

const { useState, useActions } = createNamespacedHelpers('contractNew');

export default {
  name: 'ContractForm',
  components: {
    TextInput,
    Calendar,
    ToggleBox,
    SelectInput,
    FontAwesomeIcon,
  },
  props: {
    edit: {
      type: Boolean,
      default: true,
    },
    value: Boolean,
  },
  setup(props, { refs, emit }) {
    const { order, contract, products, promotion } = useState(['order', 'contract', 'products', 'promotion']);
    const { setContract } = useActions(['setContract']);

    const maturesDateInput = ref(contract.value.maturesDate);
    const showMaturesDateBox = () => {
      refs.contractMaturesDateBox.toggle(true);
    };
    const setMaturesDateInput = (val) => {
      maturesDateInput.value = moment(val, DATE_FORMAT).format(DISPLAY_DATE_FORMAT);
      refs.contractMaturesDateBox.toggle(false);
    };

    const productInput = ref('');
    const productItems = computed(() => {
      const productDesc = Object.keys(products.value);

      const [monthPrepaid, monthPostpaid, weekPrepaid, weekPostpaid] = productDesc.reduce(([monthPrepaidProduct, monthPostpaidProduct, weekPrepaidProduct, weekPostpaidProduct], desc) => {
        const product = products.value[desc];

        if (product.prepaid) {
          if (product.repayEvery === 30) {
            return [[...monthPrepaidProduct, desc], monthPostpaidProduct, weekPrepaidProduct, weekPostpaidProduct];
          }
          return [monthPrepaidProduct, monthPostpaidProduct, [...weekPrepaidProduct, desc], weekPostpaidProduct];
        }

        if (product.repayEvery === 30) {
          return [monthPrepaidProduct, [...monthPostpaidProduct, desc], weekPrepaidProduct, weekPostpaidProduct];
        }
        return [monthPrepaidProduct, monthPostpaidProduct, weekPrepaidProduct, [...weekPostpaidProduct, desc]];
      }, [[], [], [], []]);

      return [
        ...monthPrepaid,
        ...monthPostpaid,
        ...weekPrepaid,
        ...weekPostpaid,
      ].map((item) => ({
        text: item,
        value: item
      }));
    });

    const dueDate = computed(() => {
      const chossedProduct = products.value[productInput.value];
      if (chossedProduct === undefined) {
        return 0;
      }
      return moment(maturesDateInput.value, DISPLAY_DATE_FORMAT).add(chossedProduct.repayEvery, 'days').format(DISPLAY_DATE_FORMAT);
    });

    const specialInterestValueInput = ref('');
    const interestValueDisabled = ref(true);
    const validateSpecialInterestValueInput = (val) => {
      const chossedProduct = products.value[productInput.value];
      if (chossedProduct === undefined) {
        return '';
      }

      if (chossedProduct.prepaid) {
        if (val <= 0 || val > 7.4 || val === '') {
          return 'INVALID_INTEREST_VALUE_PREPAID';
        }
      }

      if (val <= 0 || val > 8 || val === '') {
        return 'INVALID_INTEREST_VALUE_POSTPAID';
      }
      return '';
    };

    const approvedAmountInput = ref(9);
    const validateAmount = (val) => (/^-?\d*(\.[0-9]{1,3})?$/.test(val) ? '' : 'INVALID_DATA');
    const validateApproveAmount = (val) => {
      if (val * MILLION > order.value.proposedAmount * MILLION) {
        return 'INVALID_APPROVE_AMOUNT';
      }
      return validateAmount(val);
    };

    const roundingFeeInput = computed(() => {
      const chossedProduct = products.value[productInput.value];
      if (chossedProduct !== undefined) {
        return getRoundFee(approvedAmountInput.value, specialInterestValueInput.value, chossedProduct.prepaid);
      }
      return 0;
    });

    const promotionName = ref(promotion.value.promotionName);

    const principalAmount = computed(() => {
      const chossedProduct = products.value[productInput.value];
      if (chossedProduct === undefined) {
        return 0;
      }
      return getRoundPrincipal(approvedAmountInput.value, specialInterestValueInput.value, chossedProduct.prepaid);
    });

    const warehousingFeeInput = ref(0);

    const interestAmount = computed(() => {
      const chossedProduct = products.value[productInput.value];
      if (chossedProduct === undefined) {
        return 0;
      }
      return getInterestValue(approvedAmountInput.value, specialInterestValueInput.value, chossedProduct.prepaid);
    });

    const interestDiscountAmount = computed(() => getInterestDiscountAmount(
      approvedAmountInput.value,
      interestAmount.value,
      promotion.value.interestDiscount,
      promotion.value.maxDiscount,
      promotion.value.minDiscount,
      promotion.value.minPayout
    ));

    const receivedAmount = computed(() => {
      let value = approvedAmountInput.value * MILLION - warehousingFeeInput.value * MILLION;
      const chossedProduct = products.value[productInput.value];
      if (chossedProduct !== undefined) {
        if (chossedProduct.prepaid) {
          value -= interestAmount.value * MILLION;
          value += interestDiscountAmount.value * MILLION;
        }
      }
      return value / MILLION;
    });

    watch(productInput, () => {
      const chossedProduct = products.value[productInput.value];
      if (chossedProduct !== undefined) {
        specialInterestValueInput.value = chossedProduct.interestValue;
        if (specialInterestValueInput.value === null) {
          specialInterestValueInput.value = contract.value.specialInterestValue;
          interestValueDisabled.value = false;
        } else {
          interestValueDisabled.value = true;
        }
      }
    });

    const setData = () => {
      setContract({
        approvedAmount: approvedAmountInput.value,
        product: productInput.value,
        specialInterestValue: specialInterestValueInput.value,
        interestDiscountAmount: interestDiscountAmount.value,
        dueDate: dueDate.value,
        principalAmount: principalAmount.value,
        interestAmount: interestAmount.value,
      });
    };

    const setDataFromState = () => {
      approvedAmountInput.value = contract.value.approvedAmount;
      productInput.value = contract.value.product;
      specialInterestValueInput.value = contract.value.specialInterestValue;
      refs.approveAmountTextInput.validateInput();
    };

    watch(order, setDataFromState);
    watch(contract, setDataFromState);

    onMounted(() => {
      setDataFromState();
      refs.productSelectInput.validateInput();
    });

    const valid = computed(() => {
      const conditions = [
        productInput.value !== null,
        approvedAmountInput.value * MILLION <= order.value.proposedAmount * MILLION,
      ];

      const chossedProduct = products.value[productInput.value];
      if (chossedProduct !== undefined) {
        if (chossedProduct.prepaid) {
          conditions.push(specialInterestValueInput.value > 0 && specialInterestValueInput.value <= 7.4);
        } else {
          conditions.push(specialInterestValueInput.value > 0 && specialInterestValueInput.value <= 8);
        }
      }

      return !conditions.includes(false);
    });

    watch(valid, () => emit('input', valid.value));

    return {
      maturesDateInput,
      showMaturesDateBox,
      setMaturesDateInput,
      productInput,
      productItems,
      dueDate,
      specialInterestValueInput,
      interestValueDisabled,
      validateSpecialInterestValueInput,
      approvedAmountInput,
      validateApproveAmount,
      roundingFeeInput,
      principalAmount,
      promotionName,
      warehousingFeeInput,
      interestDiscountAmount,
      interestAmount,
      receivedAmount,
      setData,
      faCalendarAlt,
      moment,
      DATE_FORMAT,
      DISPLAY_DATE_FORMAT,
      changeDigitToViText,
    };
  },
};
</script>

<style lang='scss' scoped>
@import "~@/assets/scss/colors";

#form-step {
  .row {
    margin-bottom: 30px;
  }

  .floating-label {
    width: 100%;
    position: relative;
    bottom: 10px;
    .select-input {
      width: 100%;
      small {
        position: absolute;
      }
      .display {
        border-bottom: 1px solid $alto;
        padding-left: 0px;
      }
      &:focus {
        outline: none;
        &~label {
          top:-12px;
          font-size:0.825em;
          color:$blueNcs;
        }
      }

      &:focus{
        &~.highlight{
          width: 100%;
        }
      }

      &.disabled {
        pointer-events: none;
        .display {
          border-bottom: 1px solid $silver;
        }
      }
    }
    label {
      color: $grey;
      font-size:14px;
      font-weight: bold;
      position:absolute;
      pointer-events:none;
      left:0px;
      top:13px;
      transition:0.2s ease all;
      -moz-transition:0.4s ease all;
      -webkit-transition:0.4s ease all;
      span {
        color: $geranium;
      }
    }

    .highlight {
      content:'';
      display: block;
      position: absolute;
      left:0;
      width: 0;
      height: 2px;
      background: $blueNcs;
      transition: all 200ms ease-out;
    }

    &.disabled {
      label {
        color: $silver;
      }
      &:focus, &:not(:placeholder-shown):valid {
        &~label {
          top:-12px;
          font-size: 0.825em;
          color:$blueNcs;
        }
      }
    }

    &.active {
      .select-input{
        &:focus, &:not(.value) {
        outline: none;
          &~label {
            top:-12px;
            font-size:0.825em;
            color:$blueNcs;
          }
        }
      }
    };
  }
}
</style>
